import React from 'react';
import { useParams } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, UserCheckIcon } from 'lucide-react';
import { getSolution, solutionPath } from '../data/solutions';
import { BOOK_DEMO_URL, GET_STARTED_URL, VC_PATH, BRAND } from '../data/dgpt';
import { usePageMeta, canonicalUrl } from '../hooks/usePageMeta';
import { PageHero } from '../components/platform/PageHero';
import { CtaButton } from '../components/ui/CtaButton';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Reveal } from '../components/ui/Reveal';
import { ProcessFlow } from '../components/platform/ProcessFlow';
import { ProcessCta } from '../components/platform/ProcessCta';
import { SolutionVisual } from '../components/solutions/SolutionVisual';
import { SolutionProof } from '../components/solutions/SolutionProof';
import { OtherSolutions } from '../components/solutions/OtherSolutions';
import { CustomizeWorkflowBand } from '../components/solutions/CustomizeWorkflowBand';
import { SOLUTION_BUILDER_TEMPLATE } from '../data/customBuilder';
import { MobileStickyCta } from '../components/dgpt/MobileStickyCta';
import { NotFound } from './NotFound';
export function SolutionPage() {
  const {
    slug = ''
  } = useParams();
  const solution = getSolution(slug);
  const path = solutionPath(slug);
  usePageMeta({
    title: solution?.page.seo.title ?? 'Solution not found | DiligenceGPT™',
    description: solution?.page.seo.description ?? 'This solution page does not exist.',
    path,
    keywords: solution?.page.seo.keywords,
    jsonLd: solution ? [{
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: canonicalUrl('/')
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'Solutions',
        item: canonicalUrl('/solutions')
      }, {
        '@type': 'ListItem',
        position: 3,
        name: solution.label,
        item: canonicalUrl(path)
      }]
    }] : []
  });
  if (!solution) return <NotFound data-id="element-2045" data-mp="2o47zzn3kqj6e" />;
  const {
    page
  } = solution;
  const isInvest = solution.id === 'invest';
  return <>
      <main id="main" data-id="element-2046" data-mp="2j5evula6g4iw">
        <PageHero headingId="solution-heading" breadcrumb={solution.label} breadcrumbParent={{
        label: 'Solutions',
        href: '/solutions'
      }} eyebrow={`${solution.label} · ${solution.descriptor}`} title={page.title} description={solution.line} actions={<>
              <CtaButton href={GET_STARTED_URL} variant="violet" size="xl" event="primary_cta_click" eventParams={{
          placement: `solution_${solution.id}_hero`,
          cta: 'get_started'
        }} className="w-full sm:w-auto" data-id="element-2048" data-mp="207kijv20n6up">
                Get Started
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2049" data-mp="3qhx4riu2u82o" />
              </CtaButton>
              {isInvest ? <CtaButton href={VC_PATH} variant="outlineDark" size="xl" className="w-full sm:w-auto" data-id="element-2050" data-mp="3ln016rfkgqx6">
                  Explore venture capital
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2051" data-mp="1ywmrmsopdxc" />
                </CtaButton> : <CtaButton href={BOOK_DEMO_URL} variant="outlineDark" size="xl" event="enterprise_demo_click" eventParams={{
          placement: `solution_${solution.id}_hero`
        }} className="w-full sm:w-auto" data-id="element-2052" data-mp="3k5b1vp2ovtxb">
                  Book a Demo
                </CtaButton>}
            </>} aside={<SolutionVisual id={page.visual} data-id="element-2053" data-mp="ayjsgksg55w7" />} data-id="element-2047" data-mp="2j5wsvk4nymrv" />

        <section aria-labelledby="audience-heading" className="border-b border-hairline-soft bg-surface" data-id="element-2054" data-mp="ax8n4gjcrq2z">
          <div className="mx-auto grid w-full max-w-[1320px] gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8 lg:py-20" data-id="element-2055" data-mp="ymkxhjqf246v">
            <div data-id="element-2056" data-mp="13gp0jrwk9s2o">
              <SectionHeading eyebrow={solution.tagsLabel} headingId="audience-heading" title={`Built for ${solution.descriptor.toLowerCase()}.`} data-id="element-2057" data-mp="3bvncrhpoxz28" />
              <Reveal as="ul" className="mt-6 flex flex-wrap gap-2" data-id="element-2058" data-mp="36v98mar7n99h">
                {solution.tags.map(tag => <li key={tag} className="rounded-lg border border-hairline bg-panel px-3 py-1.5 text-sm font-medium text-text-primary" data-id="element-2059" data-mp="31ztwpocrjnfp">
                    {tag}
                  </li>)}
              </Reveal>
            </div>
            <Reveal delay={0.05} className="rounded-2xl border border-hairline bg-panel p-6 sm:p-8" data-id="element-2060" data-mp="3pn1yvxewagdi">
              <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#9A5B13]" data-id="element-2061" data-mp="3kvtnwwxx2my8">Example workflow</p>
              <p className="mt-3 text-lg leading-relaxed text-text-primary sm:text-xl" data-id="element-2062" data-mp="3gu8ghr45j0vf">{page.example}</p>
            </Reveal>
          </div>
        </section>

        <section aria-labelledby="workflow-heading" className="border-b border-hairline-soft bg-ink" data-id="element-2063" data-mp="ch1whqhfryhw">
          <div className="mx-auto w-full max-w-[1320px] px-4 py-16 sm:px-6 lg:px-8 lg:py-20" data-id="element-2064" data-mp="1v3tejd4n7swi">
            <SectionHeading eyebrow="How it runs" headingId="workflow-heading" title="The example, step by step." description="Agents handle the collection, checking, follow-up and preparation. Your team reviews and decides." data-id="element-2065" data-mp="202d19fr298px" />
            <Reveal className="mt-10" data-id="element-2066" data-mp="2e2ficzrjny44">
              <ProcessFlow steps={page.steps} ariaLabel={`${solution.descriptor} example workflow`} emphasizeLast data-id="element-2067" data-mp="2e1cmseja17hv" />
            </Reveal>
          </div>
        </section>

        <section aria-labelledby="output-heading" className="border-b border-hairline-soft bg-surface" data-id="element-2068" data-mp="2nsh0lax7umb4">
          <div className="mx-auto grid w-full max-w-[1320px] gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-start lg:gap-16 lg:px-8 lg:py-20" data-id="element-2069" data-mp="2ruc1wff5czow">
            <div data-id="element-2070" data-mp="31rzyzc086ja8">
              <SectionHeading eyebrow="Decision output" headingId="output-heading" title={page.decisionOutput.title} data-id="element-2071" data-mp="31rgscrg0zhbi" />
              <Reveal as="ul" className="mt-8 flex flex-col gap-3" data-id="element-2072" data-mp="11qscisbhsvpw">
                {page.decisionOutput.items.map(item => <li key={item} className="flex items-start gap-3 text-[15px] text-text-primary sm:text-base" data-id="element-2073" data-mp="wsh3ukwxor99">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-violet-soft text-lime" data-id="element-2074" data-mp="1bhn0q28au5dz">
                      <CheckIcon className="h-3 w-3" strokeWidth={2.5} aria-hidden="true" data-id="element-2075" data-mp="1kfp76xasxq8n" />
                    </span>
                    {item}
                  </li>)}
              </Reveal>
            </div>
            <Reveal delay={0.05} className="flex gap-4 rounded-2xl border border-lime/25 bg-violet-soft p-6 sm:p-8" data-id="element-2076" data-mp="1udd3vsryx7tg">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-lime text-white" data-id="element-2077" data-mp="1pfur1raty8g6">
                <UserCheckIcon className="h-5 w-5" aria-hidden="true" data-id="element-2078" data-mp="2hjlt7f84au" />
              </span>
              <div data-id="element-2079" data-mp="3r8ixsamone0p">
                <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-lime" data-id="element-2080" data-mp="3r910nq3uoqhl">Who decides</p>
                <p className="mt-1 text-xl font-semibold tracking-[-0.015em] text-text-primary" data-id="element-2081" data-mp="2bnqoqbqxy98l">{page.decisionOwner}</p>
                <p className="mt-2 text-sm leading-relaxed text-text-secondary" data-id="element-2082" data-mp="1wzdqf84dseax">
                  DiligenceGPT™ prepares the output and shows what’s still open. People review the evidence and make the decision.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        {isInvest ? <section id="venture-capital" aria-labelledby="invest-vc-heading" className="dgpt-dark scroll-mt-24 border-b border-night-line bg-night" data-id="element-2083" data-mp="1sy0p9bc8gzac">
            <div className="mx-auto flex w-full max-w-[1320px] flex-col gap-8 px-4 py-16 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8 lg:py-20" data-id="element-2084" data-mp="1iz2y00nfarnn">
              <SectionHeading tone="dark" eyebrow="Where we started" headingId="invest-vc-heading" title="Venture capital is our most mature workflow." description={`Thesis fit, Deal Radar, structured diligence, IC memos and portfolio monitoring. ${BRAND.tagline}`} data-id="element-2085" data-mp="38esnsd0i586k" />
              <CtaButton href={VC_PATH} variant="violet" size="xl" className="w-full shrink-0 sm:w-auto" data-id="element-2086" data-mp="1rd5s1ofbmtpy">
                Explore venture capital
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2087" data-mp="1mcqfv06rzs9k" />
              </CtaButton>
            </div>
          </section> : null}

        <CustomizeWorkflowBand template={SOLUTION_BUILDER_TEMPLATE[solution.id]} placement={`solution_${solution.id}`} data-id="element-2088" data-mp="1ddwmrjsnlk0r" />
        <SolutionProof organizations={page.proofOrgs} data-id="element-2089" data-mp="18fuxgh4mwogu" />
        <OtherSolutions current={solution.id} data-id="element-2090" data-mp="13g8ia258wy5e" />
        <ProcessCta placement={`solution_${solution.id}`} title={`Run ${solution.descriptor.toLowerCase()} with DiligenceGPT™.`} primaryLabel="Get Started" data-id="element-2091" data-mp="13hjm21yoy5wi" />
      </main>
      <MobileStickyCta placement={`solution_${solution.id}_mobile_sticky`} data-id="element-2092" data-mp="2xtq9bz4v0d56" />
    </>;
}